import { CookieConsentConfig } from "vanilla-cookieconsent";

/**
 * Consent is shared across all piggo.se subdomains (website, app, …), so
 * visitors only answer once. The app uses the same cookie name and domain –
 * keep both configs in sync. Outside piggo.se (localhost, Netlify previews)
 * the cookie falls back to the current host, since browsers reject a
 * .piggo.se cookie there.
 */
const SHARED_DOMAIN = "piggo.se";
const isOnSharedDomain =
  location.hostname === SHARED_DOMAIN ||
  location.hostname.endsWith(`.${SHARED_DOMAIN}`);

/**
 * All config. options available here:
 * https://cookieconsent.orestbida.com/reference/configuration-reference.html
 */
export default {
  cookie: {
    // Renamed from the default "cc_cookie" when consent became shared, so
    // everyone is asked once for the new scope
    name: "piggo_consent",
    ...(isOnSharedDomain && { domain: `.${SHARED_DOMAIN}` }),
  },
  categories: {
    necessary: {
      enabled: true,
      readOnly: true,
    },
    analytics: {
      enabled: false,
    },
    marketing: {
      enabled: false,
    },
  },
  guiOptions: {
    // Bottom right: at the bottom left it covered the register form and other
    // left-hand content, and a full-width bar covered the form's buttons
    consentModal: {
      layout: "box",
      position: "bottom right",
    },
  },
  language: {
    default: "sv",
    translations: {
      sv: {
        consentModal: {
          title: "Vi använder cookies",
          description:
            "Vi mäter besök och annonser, så fler klasser och lag hittar oss.",
          acceptAllBtn: "Acceptera alla",
          acceptNecessaryBtn: "Endast nödvändiga",
          showPreferencesBtn: "Hantera preferenser",
        },
        preferencesModal: {
          title: "Hantera cookie-preferenser",
          acceptAllBtn: "Acceptera alla",
          acceptNecessaryBtn: "Endast nödvändiga",
          savePreferencesBtn: "Acceptera valda",
          closeIconLabel: "Stäng rutan",
          sections: [
            {
              title: "Vi använder cookies",
              description:
                "Piggo AB värnar om din personliga integritet och strävar efter att skydda dina personuppgifter i enlighet med gällande dataskyddslagstiftning, inklusive GDPR. Ditt val gäller på alla Piggos plattformar.",
            },
            {
              title: "Nödvändiga",
              description:
                "Dessa cookies är nödvändiga för att webbplatsen och plattformen ska fungera korrekt och kan inte väljas bort.",

              //this field will generate a toggle linked to the 'necessary' category
              linkedCategory: "necessary",
            },
            {
              title: "Analys och prestanda",
              description:
                "Dessa cookies samlar in information om hur du använder vår webbplats. All data är anonymiserad och kan inte användas för att identifiera dig.",
              linkedCategory: "analytics",
            },
            {
              title: "Marknadsföring",
              description:
                "Dessa cookies används för att leverera annonser som är relevanta för dig och dina intressen.",
              linkedCategory: "marketing",
            },
            {
              title: "Mer information",
              description:
                'För mer information angående vår cookiepolicy och dina val, vänligen se vår <a href="https://piggo.se/integritetspolicy">integritet- och cookiepolicy</a> eller <a href="mailto:hej@piggo.se">kontakta oss</a>.',
            },
          ],
        },
      },
      en: {
        consentModal: {
          title: "We use cookies",
          description: "Cookie modal description",
          acceptAllBtn: "Accept all",
          acceptNecessaryBtn: "Reject all",
          showPreferencesBtn: "Manage Individual preferences",
        },
        preferencesModal: {
          title: "Manage cookie preferences",
          acceptAllBtn: "Accept all",
          acceptNecessaryBtn: "Reject all",
          savePreferencesBtn: "Accept current selection",
          closeIconLabel: "Close modal",
          sections: [
            {
              title: "Somebody said ... cookies?",
              description: "I want one!",
            },
            {
              title: "Strictly Necessary cookies",
              description:
                "These cookies are essential for the proper functioning of the website and cannot be disabled.",

              //this field will generate a toggle linked to the 'necessary' category
              linkedCategory: "necessary",
            },
            {
              title: "Performance and Analytics",
              description:
                "These cookies collect information about how you use our website. All of the data is anonymized and cannot be used to identify you.",
              linkedCategory: "analytics",
            },
            {
              title: "More information",
              description:
                'For any queries in relation to my policy on cookies and your choices, please <a href="#contact-page">contact us</a>',
            },
          ],
        },
      },
    },
  },
} as CookieConsentConfig;
